import axios from "axios";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  clearAuthStorage,
  fetchProfile,
  getAuthUser,
  updateProfile,
  type UpdateProfilePayload,
} from "../../../api/auth";
import type { AccountDetails, AuthContextType } from "./AuthContext";
import { AuthContext } from "./AuthContext";
import { AccountRole } from "../utils/types";

interface AuthContextProviderProps {
  children: ReactNode;
}

export function AuthContextProvider({ children }: AuthContextProviderProps) {
  const [user, setUser] = useState(() => getAuthUser());
  const token = user?.token ?? null;

  const clearSession = useCallback(() => {
    clearAuthStorage();
    setUser(null);
    setDetails(null);
    setRole(null);
    setHasCSASubscription(false);
    setDetailsError(null);
    setDetailsLoading(false);
  }, []);

  const refreshAuth = useCallback(() => {
    setUser(getAuthUser());
  }, []);

  const [details, setDetails] = useState<AccountDetails | null>(null);
  const [role, setRole] = useState<AccountRole | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [detailsError, setDetailsError] = useState<unknown>(null);
  const [hasCSASubscription, setHasCSASubscription] = useState(false);

  const refetchDetails = useCallback(async () => {
    if (!token) {
      setDetails(null);
      setRole(null);
      setHasCSASubscription(false);
      setDetailsLoading(false);
      setDetailsError(null);
      return;
    }
    setDetailsLoading(true);
    setDetailsError(null);
    setDetails(null);
    setRole(null);
    try {
      const profile = await fetchProfile(token);
      const profileRole = (profile.role ?? "") as AccountRole | "";
      setRole(profileRole || null);

      setDetails({
        subscriptionHistory: profile.subscriptionHistory,
        ...(profileRole !== "business" && {
          firstName: profile.firstName,
          secondName: profile.secondName,
          lastName: profile.surname,
        }),
        email: profile.email,
        phoneNumber: profile.phoneNumber,
        ...(profileRole === "business" && {
          companyName: profile.companyName,
          companyRegistrationNumber: profile.companyRegistrationNumber,
          address: profile.address,
          postalCode: profile.postalCode,
          city: profile.city,
          shippingHeavyPrice: profile.shippingHeavyPrice,
          shippingLightPrice: profile.shippingLightPrice,
        }),
      });
      setHasCSASubscription(profile.hasCSASubscription ?? false);
    } catch (err) {
      if (
        axios.isAxiosError(err) &&
        (err.response?.status === 401 || err.response?.status === 403)
      ) {
        clearSession();
        return;
      }
      setDetailsError(err);
      setDetails(null);
      setRole(null);
    } finally {
      setDetailsLoading(false);
    }
  }, [token, clearSession]);

  const saveAccountProfile = useCallback(
    async (payload: UpdateProfilePayload) => {
      if (!token) {
        throw new Error("Not logged in");
      }
      await updateProfile(token, payload);
      await refetchDetails();
    },
    [token, refetchDetails],
  );

  useEffect(() => {
    refetchDetails();
  }, [refetchDetails]);

  const value: AuthContextType = {
    user: user ?? null,
    token,
    userId: user?.userId ?? null,
    role,
    isAdmin: role === AccountRole.Admin,
    isEmployee: role === AccountRole.Employee,
    isBusiness: role === AccountRole.Business,
    isSocial: role === AccountRole.Social,
    isSuperUser: role === AccountRole.SuperUser,
    isAuthenticated: Boolean(token && details && !detailsError),
    hasCSASubscription: hasCSASubscription,
    details,
    detailsLoading,
    detailsError,
    refetchDetails,
    saveAccountProfile,
    refreshAuth,
    logout: () => {
      clearSession();
      window.location.replace("/");
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
